import React, { Component } from 'react';
import { Spin, Button, Upload, Icon, Tag } from 'antd';
import _notification from 'modules/notification';
import _AnswerKeyService from 'modules/AnswerKeyService';
import moment from 'moment';

// Phase C.9 (Answer Key & Lightweight Online Objection Workflow).
//
// Candidate Login artifact — reached from the dashboard (HomePageSummary_new.jsx), never a public/
// anonymous surface. Everything shown here (which Answer Key(s), whether the objection window is
// open) is exactly what the authenticated GetForCandidate endpoint returns; PostId/ClassRoomId are
// never chosen or supplied by this component — the backend derives them from the candidate's own
// Student record. This screen only records objections and shows their status; it never itself
// performs examination corrections (the separate Examination Platform remains the source of truth).
const STATUS_LABELS = {
    Submitted: { color: 'blue', text: 'Submitted' },
    UnderReview: { color: 'orange', text: 'Under Review' },
    Accepted: { color: 'green', text: 'Accepted' },
    Rejected: { color: 'red', text: 'Rejected' },
    Resolved: { color: 'purple', text: 'Resolved' }
};

class AnswerKey extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            answerKeys: [],
            objections: [],
            showObjectionForm: false,
            selectedAnswerKeyId: null,
            questionReference: '',
            objectionText: '',
            proofFile: null,
            submitting: false
        };
    }

    componentDidMount() {
        this.loadAll();
    }

    loadAll = async () => {
        this.setState({ loading: true });
        try {
            const [answerKeyResp, objectionResp] = await Promise.all([
                _AnswerKeyService.getForCandidate(),
                _AnswerKeyService.getMyObjections()
            ]);
            this.setState({
                answerKeys: (answerKeyResp.status === 200 && answerKeyResp.data) || [],
                objections: (objectionResp.status === 200 && objectionResp.data) || [],
                loading: false
            });
        } catch (e) {
            this.setState({ loading: false });
            _notification.showMessage('error', 'Something went wrong. Please try again.');
        }
    }

    openObjectionForm = (answerKeyId) => {
        this.setState({ showObjectionForm: true, selectedAnswerKeyId: answerKeyId, questionReference: '', objectionText: '', proofFile: null });
    }

    handleProofSelect = (file) => {
        const isValidType = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'application/pdf';
        if (!isValidType) {
            _notification.showMessage('error', 'Proof document must be JPG, PNG, or PDF.');
            return false;
        }
        const isLt5MB = file.size / 1024 / 1024 < 5;
        if (!isLt5MB) {
            _notification.showMessage('error', 'Proof document must be smaller than 5MB.');
            return false;
        }
        this.setState({ proofFile: file });
        return false; // prevent antd's own auto-upload — we submit manually
    }

    handleSubmitObjection = async () => {
        const { selectedAnswerKeyId, questionReference, objectionText, proofFile } = this.state;
        if (!questionReference.trim() || !objectionText.trim()) {
            _notification.showMessage('error', 'Please enter the question reference and your objection.');
            return;
        }
        this.setState({ submitting: true });
        try {
            const response = await _AnswerKeyService.submitObjection({
                AnswerKeyId: selectedAnswerKeyId,
                QuestionReference: questionReference.trim(),
                ObjectionText: objectionText.trim()
            }, proofFile);
            if (response.status === 200 && response.data) {
                _notification.showMessage('success', `Objection submitted successfully. Reference: ${response.data.ReferenceNumber}`);
                this.setState({ submitting: false, showObjectionForm: false });
                this.loadAll();
            } else {
                this.setState({ submitting: false });
                _notification.showMessage('error', (response.data && response.data.Message) || 'Could not submit objection.');
            }
        } catch (e) {
            this.setState({ submitting: false });
            const message = (e && e.response && e.response.data) || 'Something went wrong. Please try again.';
            _notification.showMessage('error', typeof message === 'string' ? message : 'Could not submit objection.');
        }
    }

    viewProof = async (objectionId) => {
        try {
            const response = await _AnswerKeyService.getMyProofAccessUrl(objectionId);
            if (response.status === 200 && response.data && response.data.Url) {
                window.open(response.data.Url, '_blank');
            } else {
                _notification.showMessage('error', 'Could not open proof document.');
            }
        } catch (e) {
            _notification.showMessage('error', 'Could not open proof document.');
        }
    }

    render() {
        const { loading, answerKeys, objections, showObjectionForm, submitting, proofFile } = this.state;

        if (loading) return <div role="status" aria-live="polite"><Spin size="large" /></div>;

        const firstAnswerKey = answerKeys.find(a => a.AnswerKeyType === 'First');
        const finalAnswerKey = answerKeys.find(a => a.AnswerKeyType === 'Final');

        return (
            <div className="candidate-result ch-container gl-bxbr analysisWrap p-4 mt-4">
                <h4 className="ch-heading4 mb-4 text-center">Answer Key</h4>

                {answerKeys.length === 0 && (
                    <div className="candidateform-cover pt-3 pr-3 pl-3 pb-3 candidate-result-wrap text-center">
                        <p className="mb-0">The Answer Key for your batch has not been published yet. Please check back later.</p>
                    </div>
                )}

                {answerKeys.map(ak => (
                    <div key={ak.Id} className="candidateform-cover pt-3 pr-3 pl-3 pb-3 mb-3 candidate-result-wrap">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <b>{ak.AnswerKeyType === 'First' ? 'First Answer Key' : 'Final Answer Key'}</b>
                                <div style={{ fontSize: 13, color: '#666' }}>Published: {moment(ak.CreatedAt).format('DD-MMM-YYYY hh:mm A')}</div>
                            </div>
                            <div className="mt-2 mt-md-0">
                                <Button className="button-global button-primary autoHeight small mr-2" onClick={() => window.open(ak.DocumentAccessUrl, '_blank')}>
                                    View {ak.AnswerKeyType} Answer Key
                                </Button>
                                {ak.AnswerKeyType === 'First' && ak.IsObjectionWindowOpen && (
                                    <Button className="button-global button-border autoHeight small" onClick={() => this.openObjectionForm(ak.Id)}>
                                        Raise Objection
                                    </Button>
                                )}
                            </div>
                        </div>
                        {ak.AnswerKeyType === 'First' && (
                            <div className="mt-2" style={{ fontSize: 13 }}>
                                {ak.IsObjectionWindowOpen ? (
                                    <span style={{ color: '#2e7d32' }}>
                                        Objection window open{ak.ObjectionWindowEnd ? (' until ' + moment(ak.ObjectionWindowEnd).format('DD-MMM-YYYY hh:mm A')) : ''}.
                                    </span>
                                ) : (
                                    <span style={{ color: '#999' }}>
                                        {ak.ObjectionWindowStart && ak.ObjectionWindowEnd
                                            ? `Objection window: ${moment(ak.ObjectionWindowStart).format('DD-MMM-YYYY hh:mm A')} to ${moment(ak.ObjectionWindowEnd).format('DD-MMM-YYYY hh:mm A')} (currently closed).`
                                            : 'Objection window is not open.'}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                ))}

                {showObjectionForm && (
                    <div className="candidateform-cover pt-3 pr-3 pl-3 pb-3 mb-3 candidate-result-wrap">
                        <h5 className="mb-3">Raise Objection</h5>
                        <div className="mb-3">
                            <label className="font-weight-bold d-block mb-1">Question Reference <span className="color-red">*</span></label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. Question 12"
                                value={this.state.questionReference}
                                onChange={(e) => this.setState({ questionReference: e.target.value })}
                                maxLength={200}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="font-weight-bold d-block mb-1">Objection / Reason <span className="color-red">*</span></label>
                            <textarea
                                className="form-control"
                                rows={4}
                                placeholder="Explain why you believe this answer is incorrect"
                                value={this.state.objectionText}
                                onChange={(e) => this.setState({ objectionText: e.target.value })}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="font-weight-bold d-block mb-1">Supporting Proof (optional — JPG/PNG/PDF, max 5MB)</label>
                            <Upload beforeUpload={this.handleProofSelect} showUploadList={false} accept=".jpg,.jpeg,.png,.pdf">
                                <Button className="button-global button-border autoHeight small"><Icon type="upload" /> Choose File</Button>
                            </Upload>
                            {proofFile && <div className="mt-1" style={{ fontSize: 13 }}>{proofFile.name}</div>}
                        </div>
                        <Button
                            className="button-global button-primary autoHeight small mr-2"
                            onClick={this.handleSubmitObjection}
                            disabled={submitting}
                        >
                            {submitting ? 'Submitting…' : 'Submit Objection'}
                        </Button>
                        <Button
                            className="button-global button-border autoHeight small"
                            onClick={() => this.setState({ showObjectionForm: false })}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                    </div>
                )}

                {objections.length > 0 && (
                    <div className="candidateform-cover pt-3 pr-3 pl-3 pb-3 mb-3 candidate-result-wrap">
                        <h5 className="mb-3">My Objections</h5>
                        <div className="table-responsive">
                            <table className="table">
                                <thead>
                                    <tr><th>Reference</th><th>Question</th><th>Submitted</th><th>Status</th><th>Resolution</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {objections.map(o => (
                                        <tr key={o.Id}>
                                            <td>{o.ReferenceNumber}</td>
                                            <td>{o.QuestionReference}</td>
                                            <td>{moment(o.SubmittedAt).format('DD-MMM-YYYY hh:mm A')}</td>
                                            <td><Tag color={(STATUS_LABELS[o.Status] || {}).color}>{(STATUS_LABELS[o.Status] || {}).text || o.Status}</Tag></td>
                                            <td>{o.ResolutionNote || '-'}</td>
                                            <td>{o.HasProof && <a href="#!" onClick={(e) => { e.preventDefault(); this.viewProof(o.Id); }}>View Proof</a>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <div className="row mt-4 footer-buttons">
                    <div className="col-12">
                        <div className="form-action d-flex justify-content-center">
                            <Button className="button-global button-border autoHeight ml-4 autoWidth" onClick={() => this.props.onTabOrderChange(-1)}>
                                Back to Dashboard
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default AnswerKey;
